/**
 * Task Management Data Service
 * EXACT COPY of ProfileStrengthResults.jsx data fetching and scoring logic
 */

import GMBService from './GMBService';
import VelocityService from './VelocityService';
import GMBFeedService from './GMBFeedService';
import PhoneValidationService from './PhoneValidationService';
import CompetitorDiscoveryService from './CompetitorDiscoveryService';
import Papa from 'papaparse';

import logger from '../utils/logger';
class TaskManagementDataService {
  constructor() {
    this.defaultKeywords = ['business', 'service', 'local'];
  }

  /**
   * EXACT COPY of ProfileStrengthResults computeScore function
   * This produces the same comprehensive data as the Profile Strength page
   */
  async getComprehensiveProfileData(mockAccount, mockBusiness, selectedKeywords = []) {
    try {
      logger.log('[TaskManagementDataService] Starting EXACT ProfileStrengthResults logic...');
      
      // Get GMB accounts and locations (EXACT same approach as ProfileStrengthResults)
      const accounts = await GMBService.getAccounts();
      if (!accounts || accounts.length === 0) {
        throw new Error('No GMB accounts found');
      }
      
      const account = accounts[0];
      const locationsList = await GMBService.getLocations(undefined, account.name);
      if (!locationsList || locationsList.length === 0) {
        throw new Error('No locations found');
      }
      
      // Find target location (same logic as ProfileStrengthResults)
      let targetLocation = locationsList.find(loc => 
        loc.title?.toLowerCase().includes(mockBusiness.name.toLowerCase())
      ) || locationsList[0];
      
      logger.log('[TaskManagement] Target location:', targetLocation.title);
      
      // Fetch location attributes (EXACT same API call as ProfileStrengthResults)
      try {
        if (targetLocation?.name) {
          const attrs = await GMBService.getLocationAttributes(targetLocation.name);
          targetLocation.attributesApi = attrs;
          logger.log('[TaskManagement] Attributes fetched:', attrs?.length || 0);
        }
      } catch (e) {
        logger.warn('[TaskManagement] Failed to fetch attributes:', e?.message);
      }
      
      // Create state objects exactly like ProfileStrengthResults
      const state = {
        account: {
          name: account.name,
          verificationState: 'VERIFIED'
        },
        business: {
          title: targetLocation.title,
          businessName: targetLocation.title,
          verified: true,
          status: 'verified'
        },
        selected: {
          title: targetLocation.title,
          verified: true
        }
      };
      
      const keywordsState = selectedKeywords.length > 0 ? selectedKeywords : ['business', 'service'];
      
      // EXACT scoring logic from ProfileStrengthResults computeScore function
      let score = 0;
      let verPts = 0, namePts = 0, addrPts = 0, phonePts = 0;
      const scoreBreakdown = [];
      
      // Build display address (EXACT same logic as ProfileStrengthResults)
      let displayAddress = '';
      if (targetLocation?.storefrontAddress) {
        const sa = targetLocation.storefrontAddress;
        const parts = [];
        if (Array.isArray(sa.addressLines) && sa.addressLines.length) parts.push(sa.addressLines.join(', '));
        if (sa.locality) parts.push(sa.locality);
        const tail = [sa.administrativeArea, sa.postalCode].filter(Boolean).join(' ');
        if (tail) parts.push(tail);
        displayAddress = parts.join(', ').trim();
      }

      // 1) Verification status (EXACT same logic as ProfileStrengthResults)
      const verificationStateRaw = account?.verificationState || '';
      const verificationState = String(verificationStateRaw).toUpperCase();
      const dashStatus = String(state.business?.status || state?.selected?.status || '').toLowerCase();
      const dashVerified = (state.business?.verified === true) || (state?.selected?.verified === true);
      const isVerified = (
        dashVerified ||
        dashStatus === 'verified' ||
        verificationState === 'VERIFIED' ||
        String(verificationStateRaw).toLowerCase() === 'verified' ||
        targetLocation?.metadata?.verified === true
      );
      
      const isSoftSuspended = (
        (targetLocation?.metadata?.placeSuspensionReason || '').toString().toUpperCase().includes('SOFT') ||
        (targetLocation?.metadata?.state || '').toString().toUpperCase().includes('SOFT')
      );
      const isUnverified = !isVerified && !isSoftSuspended;
      if (isVerified) verPts = 0;
      else if (isSoftSuspended) verPts = -60;
      else if (isUnverified) verPts = -100;
      score += verPts;

      // 2) Business name contains city (EXACT same logic)
      const businessTitle = targetLocation?.title || state.business?.title || state.business?.businessName || '';
      const city = targetLocation?.storefrontAddress?.locality || '';
      if (businessTitle && city && businessTitle.toLowerCase().includes(city.toLowerCase())) {
        namePts = 20;
      } else if (businessTitle) {
        namePts = 10;
      }
      score += namePts;

      // 3) Address scoring (EXACT same logic)
      const addr = displayAddress;
      const hasCity = !!city && addr.toLowerCase().includes(city.toLowerCase());
      const keywords = (keywordsState && keywordsState.length ? keywordsState : [])
        .map(k => k.toLowerCase());
      const hasKeyword = keywords.some(k => addr.toLowerCase().includes(k));
      const hasAnyAddressFields = !!(targetLocation?.storefrontAddress?.locality || targetLocation?.storefrontAddress?.administrativeArea || targetLocation?.storefrontAddress?.postalCode);
      if (hasCity && hasKeyword) {
        addrPts = 20;
      } else if (addr) {
        addrPts = 12;
      } else if (!hasAnyAddressFields) {
        addrPts = 2;
      }
      score += addrPts;

      // 4) Phone number with uniqueness check (EXACT same logic)
      const primaryPhone = targetLocation?.phoneNumbers?.primaryPhone || 
                          targetLocation?.nationalPhoneNumber || 
                          targetLocation?.internationalPhoneNumber ||
                          state.business?.phoneNumber ||
                          state.business?.phone;
      
      let phoneScoreResult = { score: 0, analysis: { hasPhone: false } };
      
      if (primaryPhone) {
        try {
          const currentPlaceId = targetLocation?.metadata?.placeId || 
                               targetLocation?.placeId || 
                               state.business?.placeId || 
                               state.business?.place_id ||
                               state?.locationData?.placeId ||
                               state?.selectedLocation?.placeId;
          
          phoneScoreResult = await PhoneValidationService.calculatePhoneScore(primaryPhone, currentPlaceId);
          logger.log('[TaskManagement] Phone validation result:', phoneScoreResult);
        } catch (error) {
          logger.warn('[TaskManagement] Phone validation failed, using fallback scoring:', error);
          phoneScoreResult = { 
            score: 18, 
            analysis: { 
              hasPhone: true, 
              isUnique: true, 
              reason: 'Phone validation unavailable, assuming unique',
              error: error.message 
            } 
          };
        }
      }
      
      phonePts = phoneScoreResult.score;
      score += phonePts;

      // 5) Description length (EXACT same logic)
      let descPts = 0;
      const description = targetLocation?.profile?.description || '';
      const wordCount = description ? (description.trim().split(/\s+/).filter(Boolean).length) : 0;
      if (wordCount > 0 && wordCount <= 400) {
        descPts = 5;
      } else if (wordCount >= 700) {
        descPts = 0;
      } else {
        descPts = 0;
      }
      score += descPts;

      // 6) Website link presence (EXACT same logic)
      let websitePts = 0;
      const hasWebsite = !!targetLocation?.websiteUri;
      websitePts = hasWebsite ? 5 : 0;
      score += websitePts;

      // 7) Business hours (EXACT same logic)
      let hoursPts = 0;
      const hasHours = !!(targetLocation?.regularHours?.periods?.length || targetLocation?.moreHours?.length);
      hoursPts = hasHours ? 5 : 0;
      score += hoursPts;

      // 8) Labels (EXACT same logic)
      let labelPts = 0;
      const hasLabels = Array.isArray(targetLocation?.labels) && targetLocation.labels.length > 0;
      labelPts = hasLabels ? 10 : 0;
      score += labelPts;

      // 8.0) Categories scoring (EXACT same logic)
      let catBasePts = 0;
      let catExtraPts = 0;
      const catInfo = targetLocation?.categories || {};
      const primaryCategory = catInfo?.primaryCategory || catInfo?.primary || null;
      const additionalCats = Array.isArray(catInfo?.additionalCategories)
        ? catInfo.additionalCategories
        : (Array.isArray(catInfo)
            ? catInfo.slice(1)
            : []);
      const hasPrimary = !!primaryCategory;
      if (hasPrimary) catBasePts = 25;
      const extraCount = Math.max(0, Math.min(Array.isArray(additionalCats) ? additionalCats.length : 0, 2));
      catExtraPts = extraCount * 5;
      score += (catBasePts + catExtraPts);

      // Fetch additional data (EXACT same API calls as ProfileStrengthResults)
      const accountName = account?.name || '';
      const locationName = targetLocation?.name || '';
      const accountId = (accountName && accountName.includes('accounts/')) ? accountName.split('/')[1] : undefined;
      const locationIdFromName = locationName ? String(locationName).split('/').pop() : undefined;

      // Build comprehensive business profile with additional GMB features
      const businessProfile = {
        name: businessTitle || 'Unknown Business',
        category: primaryCategory?.displayName || targetLocation?.categories?.[0]?.displayName || 'General',
        address: displayAddress,
        phone: primaryPhone,
        websiteUri: targetLocation?.websiteUri,
        placeId: targetLocation?.metadata?.placeId,
        accountId,
        locationId: locationIdFromName,
        coordinates: targetLocation?.latlng,
        profile: targetLocation?.profile || {},
        regularHours: targetLocation?.regularHours,
        attributes: targetLocation?.attributes || [],
        attributesApi: targetLocation?.attributesApi || [],
        rating: 0, // Will be filled by reviews
        reviewCount: 0, // Will be filled by reviews
        photos: [], // Will be filled by media
        photoCount: 0, // Will be filled by media
        verification: {
          isVerified,
          isSoftSuspended,
          verificationState: verificationStateRaw
        },
        phoneValidation: phoneScoreResult,
        // Additional GMB features analysis
        socialMediaLinks: this.analyzeSocialMediaLinks(targetLocation),
        logo: this.analyzeLogoPresence(targetLocation),
        qnaFeature: this.analyzeQAFeature(targetLocation),
        appointmentLink: this.analyzeAppointmentLink(targetLocation),
        specialHours: targetLocation?.specialHours || [],
        serviceAreas: targetLocation?.serviceArea || null,
        labels: targetLocation?.labels || []
      };

      // Reviews (EXACT same API call as ProfileStrengthResults)
      try {
        if (accountName && locationName) {
          const reviews = await GMBService.getReviews(undefined, accountName, locationName);
          businessProfile.reviews = reviews?.reviews || [];
          businessProfile.rating = this.calculateAverageRating(businessProfile.reviews);
          businessProfile.reviewCount = businessProfile.reviews.length;
          logger.log('[TaskManagement] Reviews fetched:', businessProfile.reviewCount);
        }
      } catch (reviewsErr) {
        logger.warn('[TaskManagement] Error fetching reviews:', reviewsErr);
        businessProfile.reviews = [];
      }

      // Media/Photos (EXACT same API call as ProfileStrengthResults)
      try {
        if (accountId && locationIdFromName) {
          let items = await GMBService.getMedia(undefined, accountId, locationIdFromName, 50);
          items = Array.isArray(items) ? items : [];
          items = items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat) && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));
          items.sort((a, b) => {
            const ta = a.createTime ? Date.parse(a.createTime) : 0;
            const tb = b.createTime ? Date.parse(b.createTime) : 0;
            return tb - ta;
          });
          businessProfile.photos = items;
          businessProfile.photoCount = items.length;
          logger.log('[TaskManagement] Media items fetched:', items.length);
        }
      } catch (mediaErr) {
        logger.warn('[TaskManagement] Error fetching media:', mediaErr);
        businessProfile.photos = [];
      }

      // Velocity Score (EXACT same API calls as ProfileStrengthResults)
      let velocityData = { velocityScore: 0, level: 'N/A', reviewsLast30Days: 0, recommendations: [] };
      try {
        if (accountId && locationIdFromName) {
          const token = await GMBService.getAccessToken();
          const { reviewsCount } = await VelocityService.fetchLast30DaysReviews(accountId, locationIdFromName, token);
          const benchmarkReviewCount = 20;
          const vResult = VelocityService.calculateVelocityScore(reviewsCount, benchmarkReviewCount);
          velocityData = {
            velocityScore: vResult.velocityScore,
            level: vResult.level,
            reviewsLast30Days: vResult.reviewsLast30Days || reviewsCount,
            recommendations: vResult.recommendations
          };
          score += velocityData.velocityScore;
          logger.log('[TaskManagement] Velocity scoring complete:', velocityData);
        }
      } catch (err) {
        logger.warn('[TaskManagement] Velocity fetch failed:', err);
      }

      // GMB Feed Score (EXACT same API calls as ProfileStrengthResults)
      let gmbFeedData = { gmbFeedScore: 0, level: 'N/A', postsLast90Days: 0, recommendations: [] };
      try {
        if (accountId && locationIdFromName) {
          const accessToken = await GMBService.getAccessToken();
          const { postsCount } = await GMBFeedService.fetchLast90DaysPosts(accountId, locationIdFromName, accessToken);
          
          const gmbFeedResult = GMBFeedService.calculateGMBFeedScore(postsCount || 0, 8);
          gmbFeedData = {
            gmbFeedScore: gmbFeedResult.feedScore,
            level: gmbFeedResult.level,
            postsLast90Days: postsCount || 0,
            recommendations: gmbFeedResult.recommendations
          };
          score += gmbFeedData.gmbFeedScore;
          logger.log('[TaskManagement] GMB Feed scoring complete:', gmbFeedData);
        }
      } catch (error) {
        logger.error('[TaskManagement] GMB Feed scoring failed:', error);
      }

      // Competitor Analysis (EXACT same approach as ProfileStrengthResults)
      let competitors = [];
      try {
        if (targetLocation?.latlng?.latitude && targetLocation?.latlng?.longitude && keywords.length > 0) {
          const keyword = keywords[0];
          competitors = await CompetitorDiscoveryService.findCompetitors({
            latitude: targetLocation.latlng.latitude,
            longitude: targetLocation.latlng.longitude,
            keyword: keyword,
            excludePlaceId: targetLocation?.metadata?.placeId
          });
          logger.log('[TaskManagement] Competitors found:', competitors.length);
        }
      } catch (error) {
        logger.warn('[TaskManagement] Competitor analysis failed:', error);
      }

      // Analyze profile gaps
      const gaps = this.analyzeProfileGaps(businessProfile);

      // Build score breakdown (EXACT same structure as ProfileStrengthResults)
      scoreBreakdown.push(
        { factor: 'Verification', details: { verified: isVerified }, points: verPts },
        { factor: 'Business name contains city', details: { hasCity: businessTitle && city && businessTitle.toLowerCase().includes(city.toLowerCase()) }, points: namePts },
        { factor: 'Address', details: { hasAddress: !!displayAddress, hasCity, hasKeyword }, points: addrPts },
        { factor: 'Phone number', details: phoneScoreResult.analysis, points: phonePts },
        { factor: 'Description', details: { wordCount }, points: descPts },
        { factor: 'Website', details: { hasWebsite }, points: websitePts },
        { factor: 'Business Hours', details: { hasHours }, points: hoursPts },
        { factor: 'Labels', details: { hasLabels }, points: labelPts },
        { factor: 'Categories', details: { hasPrimary, extraCount }, points: catBasePts + catExtraPts },
        { factor: 'Velocity Score', details: { 
          velocityScore: velocityData.velocityScore,
          reviewsLast30Days: velocityData.reviewsLast30Days,
          velocityLevel: velocityData.level,
          velocityRecommendations: velocityData.recommendations
        }, points: velocityData.velocityScore },
        { factor: 'GMB Feed', details: {
          postsLast90Days: gmbFeedData.postsLast90Days,
          gmbFeedScore: gmbFeedData.gmbFeedScore,
          gmbFeedLevel: gmbFeedData.level,
          gmbFeedRecommendations: gmbFeedData.recommendations
        }, points: gmbFeedData.gmbFeedScore },
        { factor: 'TOTAL', details: {}, points: score }
      );

      const result = {
        businessProfile,
        competitors,
        gaps,
        profileScore: score,
        scoreBreakdown,
        velocityData,
        gmbFeedData,
        lastUpdated: new Date().toISOString()
      };

      logger.log('[TaskManagementDataService] EXACT ProfileStrengthResults data complete:', {
        businessName: businessProfile.name,
        profileScore: score,
        reviewCount: businessProfile.reviewCount,
        photoCount: businessProfile.photoCount,
        competitorsFound: competitors.length,
        gapsFound: Object.keys(gaps).filter(key => gaps[key]).length
      });

      return result;

    } catch (error) {
      logger.error('[TaskManagementDataService] Error in EXACT ProfileStrengthResults logic:', error);
      throw error;
    }
  }

  /**
   * Calculate average rating from reviews array
   */
  calculateAverageRating(reviews) {
    if (!Array.isArray(reviews) || reviews.length === 0) return 0;
    const validRatings = reviews.filter(r => r.starRating && r.starRating > 0).map(r => r.starRating);
    if (validRatings.length === 0) return 0;
    return validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length;
  }

  /**
   * Analyze social media links presence
   */
  analyzeSocialMediaLinks(targetLocation) {
    // Check for social media links in various GMB fields
    const socialPlatforms = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'tiktok'];
    const foundLinks = [];
    
    // Check in profile description
    const description = targetLocation?.profile?.description || '';
    socialPlatforms.forEach(platform => {
      if (description.toLowerCase().includes(platform)) {
        foundLinks.push(platform);
      }
    });
    
    // Check in attributes (some GMB profiles have social media attributes)
    const attributes = targetLocation?.attributesApi || [];
    attributes.forEach(attr => {
      const attrName = (attr.attributeId || attr.name || '').toLowerCase();
      socialPlatforms.forEach(platform => {
        if (attrName.includes(platform) && !foundLinks.includes(platform)) {
          foundLinks.push(platform);
        }
      });
    });
    
    return {
      hasLinks: foundLinks.length > 0,
      platforms: foundLinks,
      count: foundLinks.length,
      missing: foundLinks.length === 0
    };
  }

  /**
   * Analyze logo presence in photos
   */
  analyzeLogoPresence(targetLocation) {
    // Check if there's a logo photo (usually the first photo or profile photo)
    const hasProfilePhoto = !!(targetLocation?.profile?.coverPhoto || targetLocation?.profile?.profilePhoto);
    
    return {
      hasLogo: hasProfilePhoto,
      missing: !hasProfilePhoto,
      recommendation: hasProfilePhoto ? 'Logo present' : 'Upload a high-quality logo as profile photo'
    };
  }

  /**
   * Analyze Q&A feature usage
   */
  analyzeQAFeature(targetLocation) {
    // GMB Q&A data is usually in a separate API call, but we can check for common indicators
    // For now, we'll assume it needs to be checked manually or via separate API
    return {
      hasQA: false, // Would need separate GMB Q&A API call to determine
      missing: true,
      recommendation: 'Add common questions and answers to help customers',
      suggestedQuestions: [
        'What are your business hours?',
        'Do you offer delivery or pickup?',
        'What payment methods do you accept?',
        'Do you require appointments?',
        'What services do you provide?'
      ]
    };
  }

  /**
   * Analyze appointment booking link
   */
  analyzeAppointmentLink(targetLocation) {
    // Check for appointment-related attributes or links
    const attributes = targetLocation?.attributesApi || [];
    const websiteUri = targetLocation?.websiteUri || '';
    
    // Look for appointment-related attributes
    const appointmentAttributes = attributes.filter(attr => {
      const attrName = (attr.attributeId || attr.name || '').toLowerCase();
      return attrName.includes('appointment') || attrName.includes('booking') || attrName.includes('reservation');
    });
    
    // Check if website might have booking functionality
    const appointmentKeywords = ['book', 'appointment', 'schedule', 'reserve', 'booking'];
    const websiteHasBooking = appointmentKeywords.some(keyword => 
      websiteUri.toLowerCase().includes(keyword)
    );
    
    const hasAppointmentLink = appointmentAttributes.length > 0 || websiteHasBooking;
    
    return {
      hasAppointmentLink,
      missing: !hasAppointmentLink,
      attributes: appointmentAttributes,
      websiteHasBooking,
      recommendation: hasAppointmentLink ? 
        'Appointment booking available' : 
        'Add appointment booking link to make it easy for customers to book'
    };
  }

  /**
   * Analyze profile gaps (enhanced with additional features)
   */
  analyzeProfileGaps(businessProfile) {
    return {
      website: !businessProfile.websiteUri,
      photos: (businessProfile.photoCount || 0) < 10,
      hours: !businessProfile.regularHours?.periods?.length,
      attributes: (businessProfile.attributesApi?.length || 0) < 5,
      description: !businessProfile.profile?.description,
      phone: !businessProfile.phone,
      reviews: (businessProfile.reviewCount || 0) < 10,
      posts: true, // Assume missing for now
      velocity: (businessProfile.reviewCount || 0) < 5,
      verification: !businessProfile.verification?.isVerified,
      categories: !businessProfile.category || businessProfile.category === 'General',
      // Additional features
      socialMedia: businessProfile.socialMediaLinks?.missing || false,
      logo: businessProfile.logo?.missing || false,
      qna: businessProfile.qnaFeature?.missing || false,
      appointmentLink: businessProfile.appointmentLink?.missing || false
    };
  }

  /**
   * Clean outdated festival tasks from task list
   * Removes tasks for festivals that have already passed
   */
  cleanOutdatedFestivalTasks(tasks) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // List of festival keywords to check
    const festivalKeywords = [
      'dhanteras', 'diwali', 'bhai dooj', 'holi', 'dussehra', 'navratri',
      'ganesh chaturthi', 'janmashtami', 'raksha bandhan', 'karwa chauth',
      'lohri', 'makar sankranti', 'pongal', 'eid', 'christmas', 'new year',
      'republic day', 'independence day', 'guru nanak', 'maha shivratri',
      'vasant panchami', 'ugadi', 'gudi padwa', 'baisakhi', 'rama navami',
      'buddha purnima', 'onam'
    ];
    
    const cleaned = tasks.filter(task => {
      const titleLower = (task.title || '').toLowerCase();
      const descriptionLower = (task.description || '').toLowerCase();
      
      // Check if task is festival-related
      const isFestivalTask = festivalKeywords.some(keyword => 
        titleLower.includes(keyword) || descriptionLower.includes(keyword)
      );
      
      if (!isFestivalTask) {
        return true; // Keep non-festival tasks
      }
      
      // For festival tasks, check if they mention "TOMORROW" or "TODAY"
      const isUrgentFestival = titleLower.includes('tomorrow') || 
                               titleLower.includes('today') ||
                               descriptionLower.includes('tomorrow') ||
                               descriptionLower.includes('today');
      
      if (isUrgentFestival) {
        // Check task creation date - if created more than 2 days ago, it's outdated
        const taskCreatedAt = task.createdAt ? new Date(task.createdAt) : null;
        if (taskCreatedAt) {
          const daysSinceCreation = Math.ceil((today - taskCreatedAt) / (1000 * 60 * 60 * 24));
          if (daysSinceCreation > 2) {
            logger.log(`[TaskManagement] Removing outdated festival task: ${task.title} (created ${daysSinceCreation} days ago)`);
            return false; // Remove outdated urgent festival task
          }
        }
      }
      
      return true; // Keep the task
    });
    
    if (cleaned.length < tasks.length) {
      logger.log(`[TaskManagement] Cleaned ${tasks.length - cleaned.length} outdated festival tasks`);
    }
    
    return cleaned;
  }

  /**
   * Deduplicate tasks based on similar titles or categories
   */
  deduplicateTasks(tasks) {
    // First, clean outdated festival tasks
    let cleanedTasks = this.cleanOutdatedFestivalTasks(tasks);
    
    const deduped = [];
    const seenTitles = new Set();
    
    for (const task of cleanedTasks) {
      // Normalize title for comparison (remove emojis and extra words)
      const normalizedTitle = task.title
        .replace(/[🌟🎨📸💬🏪📱❓🌐🏷️✅🚀🏆📝📅]/g, '') // Remove emojis
        .toLowerCase()
        .replace(/high-quality|professional|excellent/g, '') // Remove adjectives
        .trim();
      
      // Check for logo duplicates specifically
      const isLogoTask = normalizedTitle.includes('upload') && normalizedTitle.includes('logo');
      
      if (isLogoTask) {
        // Only keep the first logo task
        if (!seenTitles.has('logo-upload')) {
          seenTitles.add('logo-upload');
          deduped.push(task);
        }
      } else if (!seenTitles.has(normalizedTitle)) {
        seenTitles.add(normalizedTitle);
        deduped.push(task);
      }
    }
    
    logger.log('[TaskManagementDataService] Deduplicated tasks:', tasks.length, '→', deduped.length);
    return deduped;
  }

  /**
   * Generate AI-powered task recommendations using Gemini AI
   * Same intelligent analysis style as your example
   */
  async generateAITaskRecommendations(comprehensiveData) {
    try {
      logger.log('[TaskManagementDataService] Generating AI task recommendations...');
      
      // Ensure comprehensiveData has all required fields
      const safeData = {
        businessProfile: comprehensiveData?.businessProfile || {},
        gaps: comprehensiveData?.gaps || {},
        velocityData: comprehensiveData?.velocityData || { velocityScore: 0, reviewsLast30Days: 0 },
        gmbFeedData: comprehensiveData?.gmbFeedData || { gmbFeedScore: 0, postsLast90Days: 0 },
        competitors: comprehensiveData?.competitors || [],
        scoreBreakdown: comprehensiveData?.scoreBreakdown || [],
        profileScore: comprehensiveData?.profileScore || 0
      };

      // AI task generation removed. Use fallback tasks only.
      logger.warn('[TaskManagementDataService] AI task generation removed, using fallback');
      const fallbackTasks = this.generateFallbackTasks(safeData);
      return this.deduplicateTasks(fallbackTasks);
      
    } catch (error) {
      logger.error('[TaskManagementDataService] Error generating AI tasks:', error);
      // Return empty array instead of throwing to prevent crashes
      const fallbackTasks = this.generateFallbackTasks(comprehensiveData || {});
      return this.deduplicateTasks(fallbackTasks);
    }
  }

  /**
   * Fallback task generation (basic version)
   */
  generateFallbackTasks(comprehensiveData) {
    // Safely extract data with defaults
    const businessProfile = comprehensiveData?.businessProfile || {};
    const gaps = comprehensiveData?.gaps || {};
    const velocityData = comprehensiveData?.velocityData || { velocityScore: 0, reviewsLast30Days: 0 };
    const gmbFeedData = comprehensiveData?.gmbFeedData || { gmbFeedScore: 0, postsLast90Days: 0 };
    const tasks = [];
    
    // Critical tasks
    if (gaps.verification) {
      tasks.push({
        id: `verification-${Date.now()}`,
        title: '🏆 Verify Business Profile (Critical)',
        description: 'Complete the verification process to unlock full GMB features and improve search visibility.',
        priority: 'Critical',
        category: 'Profile Management',
        estimatedHours: '2-4h',
        estimatedImpact: 'Very High',
        source: 'fallback-analysis',
        aiInsight: 'Unverified profiles have significantly lower search visibility'
      });
    }
    
    if (gaps.website) {
      tasks.push({
        id: `website-${Date.now()}`,
        title: '🌐 Add Professional Website',
        description: 'Add a website URL to increase credibility and provide customers with more information.',
        priority: 'Critical',
        category: 'Digital Presence',
        estimatedHours: '1-2h',
        estimatedImpact: 'High',
        source: 'fallback-analysis',
        aiInsight: 'Businesses with websites get 35% more clicks from GMB listings'
      });
    }
    
    if (velocityData.velocityScore < 8) {
      const targetReviews = 10;
      tasks.push({
        id: `velocity-${Date.now()}`,
        title: `🚀 Get ${targetReviews} Reviews`,
        description: `Only ${velocityData.reviewsLast30Days || 0} reviews in last 30 days. This is critical for local search ranking.`,
        priority: 'Critical',
        category: 'Reputation Management',
        estimatedHours: '4-6h',
        estimatedImpact: 'Very High',
        source: 'fallback-analysis',
        aiInsight: 'Review velocity is the #1 ranking factor for local search'
      });
    }
    
    return tasks;
  }

  /**
   * Export comprehensive data to CSV (same format as ProfileStrengthResults)
   */
  exportToCSV(comprehensiveData, filename) {
    try {
      const { businessProfile, scoreBreakdown, competitors, velocityData, gmbFeedData } = comprehensiveData;
      
      const rows = [];
      
      // Header/summary rows (same as ProfileStrengthResults)
      rows.push(['Report', 'Task Management - EXACT ProfileStrengthResults Data']);
      rows.push(['Generated At', new Date().toISOString()]);
      rows.push(['Account', businessProfile.name || '']);
      rows.push(['Location', businessProfile.address || '']);
      rows.push(['Total Score', `${comprehensiveData.profileScore} / 300`]);
      rows.push([]);
      
      // Column headers for breakdown
      rows.push(['Factor', 'Points', 'Details']);
      
      const toDetailString = (details) => {
        if (!details || typeof details !== 'object') return '';
        try {
          return Object.entries(details)
            .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('|') : String(v)}`)
            .join('; ');
        } catch {
          return JSON.stringify(details);
        }
      };
      
      // Score breakdown rows
      (Array.isArray(scoreBreakdown) ? scoreBreakdown : []).forEach(item => {
        rows.push([
          item?.factor ?? '',
          typeof item?.points === 'number' ? item.points : '',
          toDetailString(item?.details)
        ]);
      });
      
      rows.push([]);
      
      // Business Profile Details
      rows.push(['BUSINESS PROFILE DETAILS']);
      rows.push(['Phone', businessProfile.phone || 'Missing']);
      rows.push(['Website', businessProfile.websiteUri || 'Missing']);
      rows.push(['Rating', businessProfile.rating || 'N/A']);
      rows.push(['Review Count', businessProfile.reviewCount || 0]);
      rows.push(['Photo Count', businessProfile.photoCount || 0]);
      rows.push(['Verification Status', businessProfile.verification?.isVerified ? 'Verified' : 'Not Verified']);
      rows.push([]);
      
      // Velocity Analysis
      rows.push(['VELOCITY ANALYSIS']);
      rows.push(['Velocity Score', `${velocityData.velocityScore}/12`]);
      rows.push(['Performance Level', velocityData.level]);
      rows.push(['Reviews Last 30 Days', velocityData.reviewsLast30Days || 0]);
      velocityData.recommendations?.forEach((rec, idx) => {
        rows.push([`Velocity Recommendation ${idx + 1}`, rec]);
      });
      rows.push([]);
      
      // GMB Feed Analysis
      rows.push(['GMB FEED ANALYSIS']);
      rows.push(['Feed Score', `${gmbFeedData.gmbFeedScore}/30`]);
      rows.push(['Feed Level', gmbFeedData.level]);
      rows.push(['Posts Last 90 Days', gmbFeedData.postsLast90Days || 0]);
      gmbFeedData.recommendations?.forEach((rec, idx) => {
        rows.push([`Feed Recommendation ${idx + 1}`, rec]);
      });
      rows.push([]);
      
      // Competitor Analysis
      rows.push(['COMPETITOR ANALYSIS']);
      rows.push(['Competitors Found', competitors.length]);
      if (competitors.length > 0) {
        rows.push(['Competitor Name', 'Rating', 'Reviews', 'Category']);
        competitors.slice(0, 10).forEach(comp => {
          rows.push([
            comp.name || 'Unknown',
            comp.rating || 'N/A',
            comp.userRatingCount || 0,
            comp.category || 'N/A'
          ]);
        });
      }
      
      // Convert to CSV text
      const csv = rows
        .map(row => row.map(cell => {
          const text = cell == null ? '' : String(cell);
          const escaped = text.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        }).join(','))
        .join('\n');
      
      const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const niceName = (businessProfile.name || 'task-management').replace(/[^a-z0-9\-]+/gi, '_').toLowerCase();
      a.href = url;
      a.download = filename || `${niceName}_EXACT_profile_strength_data.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      logger.log('[TaskManagementDataService] CSV export completed:', filename);
    } catch (err) {
      logger.error('[TaskManagementDataService] CSV export failed:', err);
      throw new Error(`Failed to export CSV: ${err.message}`);
    }
  }
}

export default new TaskManagementDataService();
